import type { DoiSoatNguoi, PhienBanChot } from '@qlk/shared';
import {
  TRANG_THAI_DOI_SOAT_LABEL,
  VAI_TRO_CHIA_LABEL,
  dinhDangVnd,
  ngayDayVn,
  nhanKyDoiSoat,
} from '@qlk/shared';
import { AlertTriangle } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import {
  Avatar,
  DataTable,
  EmptyState,
  HopThoai,
  MenuThaoTac,
  Nut,
  StatusBadge,
  dinhDangTien,
  type CotBang,
  type MucThaoTac,
  type Tone,
} from '../../../ui/index.js';

const TONE_TRANG_THAI: Record<DoiSoatNguoi['trangThai'], Tone> = {
  nhap: 'neutral',
  da_chot: 'info',
  da_thanh_toan: 'ok',
};

export interface BangDoiSoatNguoiProps {
  nguoi: DoiSoatNguoi[];
  /** `true` khi đang xem theo quý: không có nút thao tác. */
  chiXem: boolean;
  dangChay: boolean;
  onDieuChinh: (n: DoiSoatNguoi) => void;
  onChot: (n: DoiSoatNguoi) => void;
  onMoLai: (n: DoiSoatNguoi) => void;
  onThanhToan: (n: DoiSoatNguoi) => void;
}

/** Phiên bản đang hiệu lực — nơi giữ số VND và đường dẫn biên bản. */
function banHieuLuc(n: DoiSoatNguoi): PhienBanChot | null {
  return n.phienBan.find((b) => b.hieuLuc) ?? null;
}

/** Một dòng cảnh báo dưới tên người: chữ ngắn, chi tiết dài nằm trong `title`. */
function ChipCanhBao({ chu, giaiThich }: { chu: string; giaiThich: string }) {
  return (
    <span
      title={giaiThich}
      className="mt-1 inline-flex items-center gap-1 rounded-chip bg-warn-nen px-1.5 py-0.5 text-phu font-medium text-warn-chu"
    >
      <AlertTriangle size={12} aria-hidden="true" className="shrink-0" />
      {chu}
    </span>
  );
}

/** Lịch sử các lần chốt của một người trong kỳ. */
function LichSuChot({ phienBan }: { phienBan: PhienBanChot[] }) {
  if (phienBan.length === 0) {
    return <p className="text-phu text-chu-phu">Kỳ này chưa chốt lần nào.</p>;
  }

  return (
    <ul className="space-y-1.5">
      {phienBan.map((b) => (
        <li
          key={b.id}
          className={`rounded-o border border-vien px-3 py-2 text-bang ${
            b.hieuLuc ? 'bg-be-mat' : 'bg-nen'
          }`}
        >
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-semibold text-chu">
              Lần {b.phienBan} · {nhanKyDoiSoat(b.period)}
            </span>
            {b.hieuLuc ? (
              <StatusBadge tone={TONE_TRANG_THAI[b.trangThai]}>
                {TRANG_THAI_DOI_SOAT_LABEL[b.trangThai]}
              </StatusBadge>
            ) : (
              <StatusBadge tone="neutral">Đã bị thay</StatusBadge>
            )}
            <span className="so-lieu text-chu-phu">
              phần chia {dinhDangTien(b.tongPhanChia)} · phải trả {dinhDangTien(b.phaiTra)}
            </span>
          </div>
          <p className="mt-1">
            <Link to={`/bien-ban/${b.id}`} className="font-medium text-nhan hover:text-nhan-dam">
              {b.hieuLuc ? 'In biên bản' : 'In biên bản (ĐÃ THAY THẾ)'}
            </Link>
            {b.phaiTraVnd !== null && (
              <span className="so-lieu ml-2 text-chu-phu">
                {dinhDangVnd(b.phaiTraVnd)}
                {b.thueTncn !== null && b.thueTncn > 0 ? ` · thuế ${dinhDangVnd(b.thueTncn)}` : ''}
                {b.thucChuyenVnd !== null ? ` · thực chuyển ${dinhDangVnd(b.thucChuyenVnd)}` : ''}
              </span>
            )}
          </p>
          <p className="mt-1 text-phu text-chu-phu">
            Chốt {ngayDayVn(b.chotAt.slice(0, 10))} bởi {b.nguoiChot ?? 'không rõ'}
            {b.thanhToanNgay ? ` · đã trả ${ngayDayVn(b.thanhToanNgay)}` : ''}
            {b.thanhToanTepChungTu ? ` · chứng từ ${b.thanhToanTepChungTu}` : ''}
          </p>
          {b.moLaiAt && (
            <p className="mt-1 text-phu text-chu-phu">
              Mở lại {ngayDayVn(b.moLaiAt.slice(0, 10))} bởi {b.nguoiMoLai ?? 'không rõ'} — lý do:{' '}
              {b.moLaiLyDo ?? '(không ghi)'}
            </p>
          )}
        </li>
      ))}
    </ul>
  );
}

/** Dòng mở rộng: từng dòng kênh × vai trò kèm công thức. */
function ChiTietNguoi({ nguoi }: { nguoi: DoiSoatNguoi }) {
  if (nguoi.dong.length === 0) {
    return (
      <p className="text-phu text-chu-phu">
        Không có dòng chia nào trong kỳ. Số phải trả chỉ gồm điều chỉnh.
      </p>
    );
  }

  return (
    <div className="relative overflow-x-auto">
      <table className="w-full min-w-[820px] border-collapse text-bang">
        <caption className="sr-only">Các dòng chia của {nguoi.tenNguoi} trong kỳ</caption>
        <thead>
          <tr className="text-left text-phu font-semibold text-chu-phu">
            <th scope="col" className="px-2 py-1.5">
              Kênh
            </th>
            <th scope="col" className="px-2 py-1.5">
              Nhóm
            </th>
            <th scope="col" className="px-2 py-1.5">
              Vai trò
            </th>
            <th scope="col" className="px-2 py-1.5 text-right">
              Thực nhận
            </th>
            <th scope="col" className="px-2 py-1.5 text-right">
              Chi phí
            </th>
            <th scope="col" className="px-2 py-1.5 text-right">
              Lợi nhuận
            </th>
            <th scope="col" className="px-2 py-1.5 text-right">
              Cơ sở chia
            </th>
            <th scope="col" className="px-2 py-1.5">
              Công thức
            </th>
            <th scope="col" className="px-2 py-1.5 text-right">
              Phần chia
            </th>
          </tr>
        </thead>
        <tbody>
          {nguoi.dong.map((d) => (
            <tr
              key={`${d.period}-${d.channelId}-${d.shareLineId}`}
              className="border-t border-vien-trong"
            >
              <td className="px-2 py-1.5">
                {d.tenKenh}
                <span className="ml-1 text-chu-phu">({d.period})</span>
              </td>
              <td className="px-2 py-1.5 text-chu-phu">{d.tenNhom ?? 'Chưa gán nhóm'}</td>
              <td className="px-2 py-1.5">{VAI_TRO_CHIA_LABEL[d.vaiTro]}</td>
              <td className="px-2 py-1.5 text-right">{dinhDangTien(d.thucNhan)}</td>
              <td className="px-2 py-1.5 text-right">{dinhDangTien(d.chiPhiVanHanh)}</td>
              <td className={`px-2 py-1.5 text-right ${d.loiNhuan < 0 ? 'text-bad-chu' : ''}`}>
                {dinhDangTien(d.loiNhuan)}
              </td>
              <td className="px-2 py-1.5 text-right">{dinhDangTien(d.coSoChia)}</td>
              <td className="px-2 py-1.5 text-chu-phu">{d.congThuc}</td>
              <td className="px-2 py-1.5 text-right font-semibold">{dinhDangTien(d.phanChia)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * Bảng đối soát theo người, mỗi dòng mở ra được để xem **từng dòng kênh × vai
 * trò** kèm công thức.
 *
 * Số hiển thị là số **đang hiệu lực**: ảnh chụp với người đã chốt, số tính trực
 * tiếp với người còn nháp. Khi hai số lệch nhau, dòng mang cảnh báo — đúng chỗ
 * người dùng cần thấy, chứ không phải một dòng chữ chung ở đầu trang.
 *
 * ## Đổi ở bước 3/3
 *
 * - Dựng trên `DataTable` thay vì một `<table>` viết tay: dòng mở rộng, ô rỗng
 *   `—` và chân bảng đếm dòng nay giống mọi bảng khác trong app.
 * - **Một** nút chính mỗi hàng theo trạng thái (Chốt / Thanh toán / Biên bản),
 *   phần còn lại vào `⋯`. Bản cũ để hai nút chữ mỗi hàng nên cột thao tác rộng
 *   hơn cột tiền.
 * - Cảnh báo "lệch số liệu nguồn" thành **chip một dòng** dưới tên, chi tiết
 *   dài nằm trong `title`: bản cũ in cả câu kèm hai con số vào ô Trạng thái nên
 *   một hàng cao gấp ba hàng bình thường.
 */
export function BangDoiSoatNguoi({
  nguoi,
  chiXem,
  dangChay,
  onDieuChinh,
  onChot,
  onMoLai,
  onThanhToan,
}: BangDoiSoatNguoiProps) {
  const [idLichSu, setIdLichSu] = useState<string | null>(null);
  const hopLichSu = idLichSu === null ? null : (nguoi.find((n) => n.userId === idLichSu) ?? null);

  const cot: CotBang<DoiSoatNguoi>[] = [
    {
      key: 'nguoi',
      header: 'Nhân sự',
      render: (n) => (
        <div className="flex items-start gap-2.5">
          <Avatar name={n.tenNguoi} color={n.avatarColor} />
          <div className="min-w-0">
            <p className="truncate font-medium text-chu">
              {n.tenNguoi}
              {n.nguoiDaLuuTru && <span className="ml-1.5 text-phu text-chu-phu">(đã nghỉ)</span>}
            </p>
            <p className="truncate text-phu text-chu-phu">{n.emailNguoi}</p>

            {n.kenhThieuNgayVanHanh.length > 0 && (
              <ChipCanhBao
                chu="Chưa chốt được: thiếu ngày vận hành"
                giaiThich={`${n.kenhThieuNgayVanHanh
                  .map((k) => k.tenKenh)
                  .join(
                    ', ',
                  )} chưa đặt ngày bắt đầu vận hành. Chi phí của các kênh đó đang bị trừ toàn bộ nên số chưa đáng tin để đóng băng.`}
              />
            )}
            {n.lech && (
              <ChipCanhBao
                chu={`Số liệu nguồn đã thay đổi sau khi chốt (${
                  n.lech.chenh > 0 ? '+' : ''
                }${dinhDangTien(n.lech.chenh)})`}
                giaiThich={`Số đã chốt ${dinhDangTien(n.lech.phanChiaCu)}; tính lại theo dữ liệu hiện tại ra ${dinhDangTien(
                  n.lech.phanChiaMoi,
                )}. Số đang hiệu lực vẫn là số đã chốt — mở lại kỳ nếu muốn chốt theo số mới.`}
              />
            )}
          </div>
        </div>
      ),
    },
    {
      key: 'vai-tro',
      header: 'Vai trò',
      render: (n) =>
        n.vaiTro.length === 0 ? null : n.vaiTro.map((v) => VAI_TRO_CHIA_LABEL[v]).join(' · '),
    },
    { key: 'so-kenh', header: 'Kênh', align: 'right', render: (n) => n.soKenh },
    {
      key: 'phan-chia',
      header: 'Phần chia',
      align: 'right',
      render: (n) => dinhDangTien(n.phanChia),
    },
    {
      key: 'dieu-chinh',
      header: 'Điều chỉnh',
      align: 'right',
      render: (n) =>
        n.dieuChinh === 0 ? null : (
          <span className={n.dieuChinh < 0 ? 'text-bad-chu' : 'text-ok-chu'}>
            {dinhDangTien(n.dieuChinh)}
          </span>
        ),
    },
    {
      key: 'thuc-tra',
      header: 'Thực trả',
      align: 'right',
      render: (n) => <span className="font-semibold">{dinhDangTien(n.phaiTra)}</span>,
    },
    {
      key: 'vnd',
      header: 'VND',
      align: 'right',
      render: (n) => {
        const vnd = banHieuLuc(n)?.phaiTraVnd ?? null;
        return vnd === null ? null : dinhDangVnd(vnd);
      },
    },
    {
      key: 'trang-thai',
      header: 'Trạng thái',
      render: (n) => (
        <StatusBadge tone={TONE_TRANG_THAI[n.trangThai]}>
          {TRANG_THAI_DOI_SOAT_LABEL[n.trangThai]}
        </StatusBadge>
      ),
    },
  ];

  if (!chiXem) {
    cot.push({
      key: 'thao-tac',
      header: <span className="sr-only">Thao tác</span>,
      align: 'right',
      render: (n) => {
        const ban = banHieuLuc(n);

        /* Một nút chính theo trạng thái; mọi thứ khác vào ⋯. */
        const nutChinh =
          n.trangThai === 'nhap' ? (
            <Nut
              kieu="chinh"
              co="nho"
              dangChay={dangChay}
              disabled={n.kenhThieuNgayVanHanh.length > 0}
              title={
                n.kenhThieuNgayVanHanh.length > 0
                  ? `Đặt ngày bắt đầu vận hành cho ${n.kenhThieuNgayVanHanh
                      .map((k) => k.tenKenh)
                      .join(', ')} trước khi chốt`
                  : undefined
              }
              onClick={() => {
                onChot(n);
              }}
            >
              Chốt
            </Nut>
          ) : n.trangThai === 'da_chot' ? (
            <Nut
              kieu="chinh"
              co="nho"
              onClick={() => {
                onThanhToan(n);
              }}
            >
              Thanh toán
            </Nut>
          ) : ban ? (
            <Link to={`/bien-ban/${ban.id}`}>
              <Nut kieu="phu" co="nho">
                Biên bản
              </Nut>
            </Link>
          ) : null;

        const muc: MucThaoTac[] = [
          {
            nhan: 'Điều chỉnh',
            /* Chỉ sửa được khi còn nháp: đã chốt là đóng băng, sai sót xử lý
               bằng một khoản điều chỉnh ở kỳ sau. */
            tat: n.trangThai !== 'nhap',
            onChon: () => {
              onDieuChinh(n);
            },
          },
          {
            nhan: 'Mở lại',
            /* Đã trả tiền thì không mở lại được — tiền đã chuyển. */
            tat: n.trangThai !== 'da_chot',
            onChon: () => {
              onMoLai(n);
            },
          },
          {
            nhan: 'Lịch sử chốt',
            tat: n.phienBan.length === 0,
            onChon: () => {
              setIdLichSu(n.userId);
            },
          },
        ];

        return (
          <div className="flex items-center justify-end gap-1.5">
            {nutChinh}
            <MenuThaoTac muc={muc} aria-label={`Thao tác với ${n.tenNguoi}`} />
          </div>
        );
      },
    });
  }

  return (
    <>
      <DataTable
        columns={cot}
        rows={nguoi}
        keyOf={(n) => n.userId}
        caption="Đối soát theo người trong kỳ"
        tongSo={nguoi.length}
        chiTiet={(n) => <ChiTietNguoi nguoi={n} />}
        nhanChiTiet={(n) => `chi tiết phần chia của ${n.tenNguoi}`}
        khiRong={
          <EmptyState
            title="Không có ai được chia trong kỳ"
            description="Đặt tỷ lệ cho quản lý/editor ở tab “Doanh thu & chia sẻ” của kênh, hoặc tỷ lệ trưởng nhóm ở tab Nhóm."
          />
        }
      />

      {hopLichSu && (
        <HopThoai
          title={`Lịch sử chốt — ${hopLichSu.tenNguoi}`}
          rong="lg"
          onDong={() => {
            setIdLichSu(null);
          }}
        >
          <LichSuChot phienBan={hopLichSu.phienBan} />
        </HopThoai>
      )}
    </>
  );
}
